/**
 * Measurement of manually drawn trendlines.
 *
 * A trendline is drawn to measure a move, so it should say what the move was. Both directions are
 * reported as a POSITIVE gain: an up-line is a long that made money, a down-line is a short that
 * made money. Reporting a downward move as a negative number would read as a loss, which is the
 * opposite of what someone drawing that line is measuring.
 *
 * The percentage is always taken against the FIRST point, because that is the entry — the capital
 * actually put at risk. Dividing by the second point would make the same move show a different
 * percentage depending on which way it was drawn.
 */

/** Marks overlays created by the manual drawing tools, as opposed to study output. */
export const MANUAL_DRAW_SOURCE = 'manualDraw';

/**
 * Measures one trendline.
 *
 * @param {number} price1 price at the first point (the entry)
 * @param {number} price2 price at the second point
 * @returns {{points:number, percent:number, isShort:boolean}|null} null when unmeasurable
 */
export function measureTrendline(price1, price2) {
  // Rejected before Number(), because Number(null), Number('') and Number([]) are all 0 — a
  // missing price would otherwise be measured as a move to zero and reported as a fabricated
  // 100% gain rather than as no measurement at all.
  const usable = v => v !== null && v !== undefined && v !== '' && typeof v !== 'boolean'
    && !Array.isArray(v) && Number.isFinite(Number(v));
  if (!usable(price1) || !usable(price2)) return null;

  const p1 = Number(price1);
  const p2 = Number(price2);

  const isShort = p2 < p1;
  // Both directions are a gain, so the magnitude is what matters.
  const points = Math.abs(p2 - p1);

  // Guard the entry price rather than letting it produce Infinity. A zero entry has no meaningful
  // percentage, and the repaired EOD data does contain genuine 0.00 bars — see the all-zero HPIL
  // rows — so this is reachable, not theoretical.
  const percent = p1 === 0 ? null : (points / Math.abs(p1)) * 100;

  return { points, percent, isShort };
}

/** Rounds for display without letting -0 or 1e-15 artefacts through. */
function fmt(n, dp = 2) {
  const r = Number(n).toFixed(dp);
  return r === `-${(0).toFixed(dp)}` ? (0).toFixed(dp) : r;
}

/**
 * On-chart badge text, e.g. {@code "+125.50 pts (+4.82%)"} or
 * {@code "+84.20 pts (+2.15% Short Gain)"}.
 */
export function formatTrendlineBadge(price1, price2) {
  const m = measureTrendline(price1, price2);
  if (!m) return '';
  const pct = m.percent === null ? 'n/a' : `+${fmt(m.percent)}%`;
  return `+${fmt(m.points)} pts (${pct}${m.isShort ? ' Short Gain' : ''})`;
}

/**
 * Pulls the manually drawn trendlines out of the overlay list, in chart order.
 *
 * Study output also uses the trendline overlay type, so filtering on type alone would sweep up
 * every area-highlight study's connector lines and report them as though someone had drawn them.
 * The manual tools tag their overlays with `source`.
 *
 * Ordered by the first point's date so "consecutive" means consecutive along the chart, not the
 * order the user happened to draw them in.
 */
export function collectManualTrendlines(overlays) {
  if (!Array.isArray(overlays)) return [];
  return overlays
    .filter(o => o
      && String(o.type || '').toLowerCase() === 'trendline'
      && o.source === MANUAL_DRAW_SOURCE
      && Number.isFinite(Number(o.price1))
      && Number.isFinite(Number(o.price2)))
    .slice()
    .sort((a, b) => String(a.date1 || '').localeCompare(String(b.date1 || '')));
}

/**
 * Cumulative statistics across consecutive trendlines.
 *
 * Percentages are SUMMED rather than compounded, because each line is measured against its own
 * entry: they are independent trades, not one position rolled forward. Compounding them would
 * describe a sequence nobody drew.
 */
export function summariseTrendlines(overlays) {
  const lines = collectManualTrendlines(overlays).map(o => {
    const m = measureTrendline(o.price1, o.price2);
    return m && { ...m, date1: o.date1, date2: o.date2, price1: Number(o.price1), price2: Number(o.price2) };
  }).filter(Boolean);

  const totalPoints = lines.reduce((s, l) => s + l.points, 0);
  const measurable = lines.filter(l => l.percent !== null);
  const totalPercent = measurable.reduce((s, l) => s + l.percent, 0);

  return {
    lines,
    totalPoints,
    totalPercent,
    // Surfaced so the summary can say the total is incomplete rather than quietly under-reporting.
    unmeasurableCount: lines.length - measurable.length,
  };
}

/**
 * Which side of the endpoint a badge sits on, alternating down the list.
 *
 * Badges stacked on the same side pile up wherever several lines end near the same bar, and the
 * numbers become unreadable exactly where the chart is busiest. Alternating gives adjacent lines
 * opposite sides, which separates them without moving either badge away from the point it
 * measures.
 *
 * Indexed by the line's position in chart order — the same numbering the journal uses — so "Line 1"
 * in the summary is the line whose badge sits on the first side.
 */
export function badgeSideForIndex(index) {
  return index % 2 === 0 ? 'left' : 'right';
}

/**
 * Maps each manual trendline's overlay id to the side its badge should take.
 *
 * Built from the chart-ordered list rather than the raw overlay array, so inserting a line in the
 * middle re-alternates the ones after it instead of leaving two neighbours sharing a side.
 */
export function badgeSidesByOverlayId(overlays) {
  const sides = {};
  collectManualTrendlines(overlays).forEach((o, i) => {
    if (o.id != null) sides[o.id] = badgeSideForIndex(i);
  });
  return sides;
}

/** Delimiters for the journal section this module owns. */
export const TRENDLINE_SECTION_START = '<!-- trendline-summary:start -->';
export const TRENDLINE_SECTION_END = '<!-- trendline-summary:end -->';

/** The Markdown block for the current set of trendlines, or '' when there are none. */
export function buildTrendlineSummaryMarkdown(overlays) {
  const { lines, totalPoints, totalPercent, unmeasurableCount } = summariseTrendlines(overlays);
  if (lines.length === 0) return '';

  let md = `${TRENDLINE_SECTION_START}\n### Trendline Move Summary\n`;
  lines.forEach((l, i) => {
    const pct = l.percent === null ? 'n/a' : `+${fmt(l.percent)}%`;
    md += `- **Line ${i + 1}:** P1 (${fmt(l.price1)}) -> P2 (${fmt(l.price2)}) `
        + `| Move: +${fmt(l.points)} pts (${pct}${l.isShort ? ' Short' : ''})\n`;
  });
  md += '\n---\n';
  md += `**Total Move Captured:** +${fmt(totalPoints)} pts | `
      + `**Cumulative Gain:** +${fmt(totalPercent)}%\n`;
  if (unmeasurableCount > 0) {
    md += `\n> ${unmeasurableCount} line(s) excluded from the percentage total `
        + `(entry price of 0 has no percentage).\n`;
  }
  md += `${TRENDLINE_SECTION_END}\n`;
  return md;
}

/**
 * Replaces the trendline section of a journal, or appends it when absent.
 *
 * Owning a delimited section rather than appending is what makes live updates possible: redrawing
 * a line rewrites its block instead of stacking another copy underneath, and everything the user
 * typed outside the markers is left exactly as it was.
 */
export function upsertTrendlineSummary(markdown, overlays) {
  const base = markdown || '';
  const section = buildTrendlineSummaryMarkdown(overlays);

  const start = base.indexOf(TRENDLINE_SECTION_START);
  const end = base.indexOf(TRENDLINE_SECTION_END);

  if (start !== -1 && end !== -1 && end > start) {
    const before = base.slice(0, start);
    const after = base.slice(end + TRENDLINE_SECTION_END.length).replace(/^\n/, '');
    // No trendlines left: drop the section entirely rather than leaving an empty heading.
    return section ? before + section + after : (before + after);
  }

  if (!section) return base;
  return base.endsWith('\n') || base === '' ? base + section : `${base}\n${section}`;
}
